import React, { useState, useEffect, useCallback } from 'react';
import { NewsService } from '../../services/NewsService';
import { NewsCard } from './NewsCard';
import { RefreshCw } from 'lucide-react';
import type { NewsArticle } from '../../types/market';

interface StockNewsProps {
  symbol: string;
  limit?: number;
  autoRefresh?: boolean;
  refreshInterval?: number;
  showSentimentFilter?: boolean;
  compact?: boolean;
  className?: string;
}

const SENTIMENTS = [
  { id: 'all',      label: 'All',      color: '#94a3b8', bg: 'rgba(148,163,184,0.1)',  border: 'rgba(148,163,184,0.2)' },
  { id: 'positive', label: 'Positive', color: '#10b981', bg: 'rgba(16,185,129,0.1)',   border: 'rgba(16,185,129,0.25)' },
  { id: 'neutral',  label: 'Neutral',  color: '#94a3b8', bg: 'rgba(148,163,184,0.1)',  border: 'rgba(148,163,184,0.2)' },
  { id: 'negative', label: 'Negative', color: '#ef4444', bg: 'rgba(239,68,68,0.1)',    border: 'rgba(239,68,68,0.25)' },
];

function timeAgo(date: Date): string {
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

export const StockNews: React.FC<StockNewsProps> = ({
  symbol,
  limit = 10,
  autoRefresh = false,
  refreshInterval = 10,
  showSentimentFilter = true,
  compact = false,
  className = '',
}) => {
  const [articles, setArticles]       = useState<NewsArticle[]>([]);
  const [loading, setLoading]         = useState(true);
  const [spinning, setSpinning]       = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [sentiment, setSentiment]     = useState('all');
  const [newsService]                 = useState(() => new NewsService());

  const dedup = useCallback((list: NewsArticle[]): NewsArticle[] => {
    const seenUrls = new Set<string>();
    const seenTitles = new Set<string>();
    return list.filter(a => {
      const t = a.title.toLowerCase().replace(/[^\w\s]/g, '').trim();
      if (seenUrls.has(a.url) || seenTitles.has(t)) return false;
      seenUrls.add(a.url); seenTitles.add(t); return true;
    });
  }, []);

  const filterRelevant = useCallback((list: NewsArticle[], ticker: string): NewsArticle[] => {
    const tl = ticker.toLowerCase();
    return list.filter(a => {
      const ttl = a.title.toLowerCase(); const snip = a.snippet.toLowerCase();
      return ttl.includes(tl) || snip.includes(tl) || ttl.includes(`$${tl}`) || snip.includes(`$${tl}`)
        || ['stock','shares','earnings','revenue','profit','loss'].some(kw => ttl.includes(kw) || snip.includes(kw));
    });
  }, []);

  const fetchNews = useCallback(async () => {
    try {
      setError(null);
      let items = await newsService.getStockNews(symbol.toUpperCase(), limit * 2);
      if (items.length === 0) {
        const market = await newsService.getMarketNews(limit * 3);
        items = filterRelevant(market, symbol);
      }
      items = dedup(items);
      if (sentiment !== 'all') items = items.filter(a => a.sentiment === sentiment);
      setArticles(items.slice(0, limit));
      setLastUpdated(new Date());
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load news. Please try again later.');
    } finally {
      setLoading(false);
      setSpinning(false);
    }
  }, [newsService, symbol, limit, sentiment, dedup, filterRelevant]);

  useEffect(() => { if (symbol) { setLoading(true); fetchNews(); } }, [fetchNews, symbol]);

  useEffect(() => {
    if (!autoRefresh || !symbol) return;
    const iv = setInterval(fetchNews, refreshInterval * 60 * 1000);
    return () => clearInterval(iv);
  }, [autoRefresh, refreshInterval, fetchNews, symbol]);

  const handleRefresh = () => { setSpinning(true); fetchNews(); };

  const FilterPill: React.FC<{ s: typeof SENTIMENTS[0] }> = ({ s }) => {
    const active = sentiment === s.id;
    return (
      <button
        onClick={() => setSentiment(s.id)}
        style={{
          padding: '0.2rem 0.65rem', borderRadius: 99,
          fontSize: '0.7rem', fontWeight: 700,
          background: active ? s.bg : 'transparent',
          border: `1px solid ${active ? s.border : '#1e2d45'}`,
          color: active ? s.color : '#334155',
          cursor: 'pointer', transition: 'all 0.18s',
        }}
        onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = '#2a3a55'; e.currentTarget.style.color = '#64748b'; } }}
        onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = '#1e2d45'; e.currentTarget.style.color = '#334155'; } }}
      >
        {s.label}
      </button>
    );
  };

  /* ── Loading skeleton ── */
  if (loading && articles.length === 0) {
    return (
      <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div className="skeleton" style={{ height: 13, width: '85%', borderRadius: 4 }} />
            <div className="skeleton" style={{ height: 11, width: '55%', borderRadius: 4 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>

      {/* ── Controls row ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
        {/* Sentiment filter */}
        {showSentimentFilter && !compact && (
          <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
            {SENTIMENTS.map(s => <FilterPill key={s.id} s={s} />)}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
          {lastUpdated && (
            <span style={{ fontSize: '0.7rem', color: '#1e3a5f', fontWeight: 500 }}>
              {timeAgo(lastUpdated)}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={spinning}
            aria-label="Refresh news"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, background: 'transparent', border: '1px solid #1e2d45', cursor: spinning ? 'not-allowed' : 'pointer', color: '#334155', transition: 'all 0.18s' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#1a2235'; e.currentTarget.style.color = '#60a5fa'; e.currentTarget.style.borderColor = 'rgba(59,130,246,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#334155'; e.currentTarget.style.borderColor = '#1e2d45'; }}
          >
            <RefreshCw size={13} strokeWidth={2} style={{ animation: spinning ? 'spin 0.7s linear infinite' : 'none' }} />
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '0.75rem', fontSize: '0.8125rem', color: '#f87171', display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01" strokeLinecap="round"/></svg>
          {error}
        </div>
      )}

      {/* ── Empty ── */}
      {articles.length === 0 && !loading && !error && (
        <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
          <p style={{ fontSize: '0.875rem', color: '#475569', fontWeight: 600, marginBottom: '0.25rem' }}>
            No news for {symbol.toUpperCase()}
          </p>
          <p style={{ fontSize: '0.78rem', color: '#1e3a5f' }}>Try refreshing or check back later</p>
        </div>
      )}

      {/* ── Articles ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 0 : '0.625rem' }}>
        {articles.map((a, i) => (
          <NewsCard
            key={`${a.url}-${i}`}
            article={a}
            showSentiment={true}
            compact={compact}
          />
        ))}
      </div>

      {/* ── Auto-refresh indicator ── */}
      {autoRefresh && lastUpdated && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, paddingTop: '0.5rem', borderTop: '1px solid #0f1929' }}>
          <div className="live-dot" style={{ width: 6, height: 6 }} />
          <span style={{ fontSize: '0.7rem', color: '#1e3a5f', fontWeight: 500 }}>
            Auto-refreshing every {refreshInterval}m
          </span>
        </div>
      )}
    </div>
  );
};
